import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import api from "../../services/api";
import { Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCrown, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

const TEAM_STATUS_STYLE = {
  forming: 'bg-orange-500/10 text-orange-dark',
  complete: 'bg-green/10 text-green',
  disqualified: 'bg-red-500/10 text-red-500',
};

const MEMBER_STATUS_STYLE = {
  accepted: 'text-green',
  invited: 'text-orange-dark',
  declined: 'text-red-500',
};

const memberName = (m) =>
  m.userId ? `${m.userId.firstName} ${m.userId.lastName}` : 'Unknown user';

export default function AdminTeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTeam(); }, [id]);

  const loadTeam = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/teams/${id}`);
      setTeam(data.data.team);
      setSubmission(data.data.submission);
    } catch (err) {
      ToastError('Failed to load team');
    } finally { setLoading(false); }
  };

  if (loading) return <div className="p-12 text-center text-grey-667">Loading...</div>;
  if (!team) return <div className="p-12 text-center text-grey-667">Team not found</div>;

  const leaderId = team.leaderId?._id || team.leaderId;
  const members = team.members || [];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/teams')} className="text-grey-667 hover:text-primary cursor-pointer">
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div>
          <H1Title size="xs">{team.name}</H1Title>
          <Paragraph size="sm" className="text-grey-667">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${TEAM_STATUS_STYLE[team.status] || 'bg-grey-bg text-grey-667'}`}>{team.status}</span>
            <span className="ml-2">Created {new Date(team.createdAt).toLocaleDateString()}</span>
          </Paragraph>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-grey-200 p-4">
          <p className="text-xs text-grey-98 uppercase tracking-wide">Leader</p>
          <p className="font-semibold mt-1">
            {team.leaderId ? `${team.leaderId.firstName} ${team.leaderId.lastName}` : '—'}
          </p>
          <p className="text-xs text-grey-667">{team.leaderId?.email}</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4">
          <p className="text-xs text-grey-98 uppercase tracking-wide">Active Members</p>
          <p className="font-semibold mt-1">{members.filter(m => m.status === 'accepted').length} / 3</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4">
          <p className="text-xs text-grey-98 uppercase tracking-wide">Invite Code</p>
          <p className="font-mono font-semibold mt-1">{team.inviteCode || '—'}</p>
        </div>
      </div>

      {/* Members */}
      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-grey-200 bg-grey-bg">
          <h3 className="font-bold text-sm">Members</h3>
        </div>
        {members.length === 0 ? (
          <div className="p-8 text-center text-grey-667 text-sm">No members</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-grey-bg border-b border-grey-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {members.map((m, i) => {
                const isLeader = String(m.userId?._id || m.userId) === String(leaderId);
                return (
                  <tr key={m.userId?._id || i} className="hover:bg-grey-bg/50">
                    <td className="px-4 py-3 font-semibold">
                      {memberName(m)}
                      {isLeader && (
                        <FontAwesomeIcon icon={faCrown} className="ml-2 text-orange text-xs" title="Team leader" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-grey-667">{m.userId?.email || '—'}</td>
                    <td className="px-4 py-3 capitalize text-grey-667">{m.role}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${MEMBER_STATUS_STYLE[m.status] || 'text-grey-667'}`}>{m.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-grey-98">
                      {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Submission */}
      <div className="bg-white rounded-xl border border-grey-200 p-4">
        <h3 className="font-bold text-sm mb-3">Submission</h3>
        {submission ? (
          <div className="flex items-center justify-between">
            <div className="text-sm text-grey-667 space-y-1">
              <p><span className="text-grey-98">Status:</span> <span className="font-medium text-grey-dark capitalize">{submission.status}</span></p>
              <p><span className="text-grey-98">Round:</span> <span className="font-medium text-grey-dark">{submission.currentRound}</span></p>
              {submission.isWinner && <p className="text-green font-semibold">Winner</p>}
            </div>
            <button
              onClick={() => navigate(`/admin/submissions/${submission._id}`)}
              className="text-primary hover:underline text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              View submission <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1 text-xs" />
            </button>
          </div>
        ) : (
          <p className="text-sm text-grey-667">This team has not started a submission yet.</p>
        )}
      </div>
    </div>
  );
}
